import { AffiliateService } from './core/data-access/services/affiliate.service';
import {
  ApplicationConfig,
  InjectionToken,
  isDevMode,
  LOCALE_ID,
  makeEnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@core/utils/interceptors/auth.interceptor';
import { provideRouterStore } from '@ngrx/router-store';
import { RouteSerializer } from '@core/utils/serializers/route.serializer';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { UserService } from '@core/data-access/services/user.service';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import * as fromCore from '@core/data-access/reducers';
import { UserEffects } from '@core/data-access/effects/user.effects';
import { UtilityEffects } from '@core/data-access/effects/utility.effects';
import { UtilityService } from '@core/data-access/services/utility.service';
import { LicenseService } from '@core/data-access/services/license.service';
import { LicenseEffects } from '@core/data-access/effects/license.effects';
import { LinkEffects } from '@core/data-access/effects/link.effects';
import { LinkService } from '@core/data-access/services/link.service';
import { PolicyEffects } from '@core/data-access/effects/policy.effects';
import { PolicyService } from '@core/data-access/services/policy.service';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import localeAr from '@angular/common/locales/ar-SA';
import { registerLocaleData } from '@angular/common';
import { SeoService } from '@core/utils/services/seo.service';
import { AffiliateEffects } from '@core/data-access/effects/affiliate.effects';
import { RouteEffects } from '@core/data-access/effects/route.effects';
import { provideScrollbarOptions } from 'ngx-scrollbar';
import { AppSettings } from '@core/utils/models/utility.model';
import { tap, BehaviorSubject, firstValueFrom, map, filter } from 'rxjs';
import { RecaptchaModule } from '@shared/ui/recaptcha/recaptcha.module';
import { FileUploaderModule } from '@shared/ui/file-uploader/file-uploader.module';

dayjs.locale('ar');
registerLocaleData(localeAr);

export const APP_SETTINGS = new InjectionToken<BehaviorSubject<AppSettings>>('app.settings');

const appSettings: BehaviorSubject<AppSettings> = new BehaviorSubject<AppSettings>({
  RECAPTCHA_SITE_KEY: '',
});

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'ar-SA' },
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    provideClientHydration(),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
    makeEnvironmentProviders([provideStore(fromCore.reducers, { metaReducers: fromCore.metaReducers })]),
    provideEffects(
      RouteEffects,
      UserEffects,
      UtilityEffects,
      LicenseEffects,
      LinkEffects,
      PolicyEffects,
      AffiliateEffects
    ),
    provideRouterStore({
      serializer: RouteSerializer,
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
    }),
    provideScrollbarOptions({
      visibility: 'hover',
      appearance: 'compact',
      thumbClass: '!rounded-full',
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeAppFactory(inject(UserService), inject(AffiliateService), inject(UtilityService));
      return initializerFn();
    }),
    {
      provide: APP_SETTINGS,
      useValue: appSettings,
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        animation: {
          enterDuration: 200,
          exitDuration: 200,
        },
      },
    },
    UserService,
    UtilityService,
    LicenseService,
    LinkService,
    PolicyService,
    SeoService,
    AffiliateService,
    importProvidersFrom(
      RecaptchaModule.init(
        appSettings.pipe(
          map(({ RECAPTCHA_SITE_KEY }) => RECAPTCHA_SITE_KEY),
          filter(Boolean),
          map((siteKey) => ({ siteKey }))
        )
      )
    ),
    importProvidersFrom(FileUploaderModule),
  ],
};

function initializeAppFactory(
  userService: UserService,
  affiliateService: AffiliateService,
  utilityService: UtilityService
): () => Promise<unknown> {
  return () => {
    userService.init();
    affiliateService.init();
    return firstValueFrom(
      utilityService.getAppSettings().pipe(
        tap((response) => {
          appSettings.next(response.data);
        })
      )
    );
  };
}
